import { useCallback, useEffect, useState } from "react";

import {
  acceptInvite as matrixAcceptInvite,
  declineInvite as matrixDeclineInvite,
  subscribeInvitedRooms,
  type Client,
  type InvitedRoom,
} from "~/lib/matrix";

export interface InvitationError {
  roomId: string;
  message: string;
}

export interface UseInvitations {
  invites: InvitedRoom[];
  pending: Map<string, "accept" | "decline">;
  error: InvitationError | null;
  accept: (roomId: string) => Promise<void>;
  decline: (roomId: string) => Promise<void>;
}

export function useInvitations(
  client: Client | null,
  selectRoom?: (roomId: string) => void,
): UseInvitations {
  const [invites, setInvites] = useState<InvitedRoom[]>([]);
  const [pending, setPending] = useState<Map<string, "accept" | "decline">>(() => new Map());
  const [error, setError] = useState<InvitationError | null>(null);

  useEffect(() => {
    if (!client) {
      setInvites([]);
      return;
    }
    return subscribeInvitedRooms(client, setInvites);
  }, [client]);

  useEffect(() => {
    if (error && !invites.some((invite) => invite.roomId === error.roomId)) setError(null);
  }, [error, invites]);

  const accept = useCallback(
    async (roomId: string) => {
      setPending((current) => new Map(current).set(roomId, "accept"));
      setError(null);
      try {
        const invite = invites.find((item) => item.roomId === roomId);
        await matrixAcceptInvite(client!, roomId, {
          isDirect: invite?.isDirect ?? false,
          inviter: invite?.inviter ?? "",
        });
        selectRoom?.(roomId);
      } catch {
        setError({ roomId, message: "Could not accept invitation. Try again." });
      } finally {
        setPending((current) => {
          const next = new Map(current);
          next.delete(roomId);
          return next;
        });
      }
    },
    [client, invites, selectRoom],
  );

  const decline = useCallback(
    async (roomId: string) => {
      setPending((current) => new Map(current).set(roomId, "decline"));
      setError(null);
      try {
        await matrixDeclineInvite(client!, roomId);
      } catch {
        setError({ roomId, message: "Could not decline invitation. Try again." });
      } finally {
        setPending((current) => {
          const next = new Map(current);
          next.delete(roomId);
          return next;
        });
      }
    },
    [client],
  );

  return { invites, pending, error, accept, decline };
}
